// astro vue will render empty page if import vue from 'vue'
// @ts-ignore
import { createApp } from 'vue/dist/vue.esm-bundler.js'
import { computed, ref } from 'vue'
import { useStore } from '@nanostores/vue'
import { isLogin } from '@/store/userStore'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import {
	type HeroSectionData,
	type HeroButtonType,
} from '@/components/HeroSection.vue'
import hybridPage from '@/lib/hybridPage'
import { getProductSharingUrl } from '@/legacy/ts/productSharing'
import { type RegistrationCardData } from '@/components/RegistrationCard.vue'
import MarqueeVue from '@/components/Marquee.vue'
import { cn, formatSrc, getCreditText } from '@/lib/utils'
import type { StepData } from '@/types/StepCheckout'
import { useTooltip } from '@/lib/useTooltip'

hybridPage()

createApp({
	name: 'ExpressCheckout',
	components: {
		BaseButton,
		MarqueeVue,
	},
	setup() {
		const isLoginGlobal = useStore(isLogin)

		// ================== hero section ==================

		const heroSectionData: HeroSectionData = window.pageContent.heroContent
		const { postSectionContent } = window.pageContent

		const currentHeroSectionData = computed(() => {
			return isLoginGlobal.value
				? heroSectionData.afterLogin
				: heroSectionData.beforeLogin
		})
		const handleHeroSectionButtonClick = (buttonType: HeroButtonType) => {
			if (buttonType === 'primary') {
				if (currentHeroSectionData.value.primaryIsRegister) {
					window.BsHeaderVm.showSignupPanel()
				} else {
					window.location.href =
						currentHeroSectionData.value.primaryBtnLink || ''
				}
			}
		}

		const handlePostSectionButtonClick = (link?: string) => {
			if (link) {
				window.location.href = link
			} else {
				const url = `${import.meta.env.PUBLIC_ROOT_URL}/buyaholic`
				window.location.href = url
			}
		}

		// ================== Flow ==================
		const Flowtitle: string = window.pageContent.flowContent.title
		const Flowdata: StepData[] = window.pageContent.flowContent.content
		const { containerRef } = useTooltip(Flowdata)

		const DEFAULTITEM = 0
		const openIndex = ref(DEFAULTITEM)

		const isActiveStep = (index: number) => openIndex.value === index
		const summaryClasses = (index: number) =>
			cn(
				'p-20px flex flex-col gap-8px',
				isActiveStep(index)
					? 'bg-t-900'
					: 'bg-transparent hover:bg-b-5% active:bg-b-5%',
				isActiveStep(index)
					? 'rounded-md'
					: 'rounded-none hover:rounded-md active:rounded-md',
				isActiveStep(index) ? ' border-0' : ' border-b-n-200 border-b-2',
			)
		const stepClasses = (index: number) =>
			cn(
				'typography-s1-emp',
				isActiveStep(index) ? 'text-t-400' : 'text-t-600',
				'hover:bg-gray-200',
			)
		const titleClasses = (index: number) =>
			cn('typography-h5-str', isActiveStep(index) ? 'text-white' : 'text-black')

		/**
		 * Function to control the expansion or collapse of steps.
		 * Clicking an already expanded step does nothing, ensuring one step is always open.
		 */
		const toggleDetail = (index: number): void => {
			// If a non-current step is clicked, switch to that step
			if (openIndex.value !== index) {
				openIndex.value = index
			}
			// If the current expanded step is clicked, do nothing (ensuring at least one step is open)
		}

		// ---------------------
		// Registration Card Section Start
		// ---------------------
		const registrationCardData: RegistrationCardData =
			window.pageContent.registrationContent

		const currentRegistrationCardContent = computed(() => {
			return isLoginGlobal.value
				? registrationCardData.afterLogin
				: registrationCardData.beforeLogin
		})

		const currentRegistrationCardTitle1 = computed(() => {
			return getCreditText(currentRegistrationCardContent.value.title1)
		})

		const currentRegistrationCardTitle2 = computed(() => {
			return getCreditText(currentRegistrationCardContent.value.title2)
		})

		const WELCOME_CREDIT = window.BUYANDSHIP?.settings?.member_welcome_credits

		const currentRegistrationCardDesc = computed(() => {
			return getCreditText(currentRegistrationCardContent.value.description)
		})

		const bgRegistrationCardClass = computed(() => {
			return currentRegistrationCardContent.value.class
		})

		const openReferral = (link?: string) => {
			if (link) {
				window.location.href = link
				return
			}
			window.location.href = getProductSharingUrl('referral')
		}

		const openRegister = () => {
			window.BsHeaderVm.showSignupPanel()
		}

		const handleRegistrationCardClick = (link?: string) => {
			if (isLoginGlobal.value) {
				openReferral(link)
			} else {
				openRegister()
			}
		}
		// ---------------------
		// Registration Card Section End
		// ---------------------

		const contentOtherService = window.pageContent.otherServiceContent

		// ---------------------
		// Remark Section
		// ---------------------
		const currentRemarkSection = window.pageContent.remarkSectionContent

		return {
			WELCOME_CREDIT,
			isLoginGlobal,
			// Hefo section
			currentHeroSectionData,
			postSectionContent,
			handleHeroSectionButtonClick,
			handlePostSectionButtonClick,
			// ================== Flow ==================
			containerRef,
			Flowtitle,
			Flowdata,
			summaryClasses,
			stepClasses,
			titleClasses,
			toggleDetail,
			openIndex,
			cn,
			formatSrc,
			// ================== Registration Card ==================
			currentRegistrationCardContent,
			currentRegistrationCardTitle1,
			currentRegistrationCardTitle2,
			currentRegistrationCardDesc,
			bgRegistrationCardClass,
			handleRegistrationCardClick,
			// ================== Other Service ==================
			contentOtherService,
			// ================== Remark Service ==================
			currentRemarkSection,
		}
	},
}).mount('#bsMain')
